'use client';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    deleteToken,
    getToken,
    onMessage,
    type MessagePayload,
    type Messaging,
} from 'firebase/messaging';

import { getMessagingIfSupported } from '@/libs/firebase';
import { useAppDispatch } from '@/redux-store/hooks';
import { setShowRoleChangeNotificationAction } from '@/redux-store/slices/role-change-notification';
import { notificationService } from '@/services/rest';
import { NotificationTypeEnum } from '@/types/enums/notification-type-enum';

const VAPID = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY || '';
const SW_PATH = '/firebase-messaging-sw.js';
const SUB_KEY = 'fcm_subscribed_token';

export function useFcm({
    enabled,
    onEvent,
    platform = 'web',
}: {
    enabled: boolean;
    onEvent?: () => void;
    platform?: string;
}) {
    const dispatch = useAppDispatch();
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const messagingRef = useRef<Messaging | null>(null);
    const prevEnabledRef = useRef<boolean | null>(null);

    const [token, setToken] = useState<string | null>(null);
    const [lastMessage, setLastMessage] = useState<MessagePayload | null>(null);

    const [permission, setPermission] =
        useState<NotificationPermission>('default');

    const trigger = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => onEvent?.(), 250);
    }, [onEvent]);

    const optOut = useCallback(async () => {
        if (typeof window === 'undefined') return;

        const cur = localStorage.getItem(SUB_KEY);

        if (cur) {
            await notificationService()
                .unSubscribeTopic({ token: cur })
                .catch(() => {});
        }

        if (messagingRef.current) {
            await deleteToken(messagingRef.current).catch(() => {});
        }

        localStorage.removeItem(SUB_KEY);
        setToken(null);
    }, []);

    useEffect(() => {
        const prevEnabled = prevEnabledRef.current;

        prevEnabledRef.current = enabled;

        // Only cleanup on real logout transition: true -> false
        if (prevEnabled === true && enabled === false) {
            void optOut();

            return;
        }

        // Don't init when disabled
        if (!enabled) return;

        const hasWindow = typeof window !== 'undefined';
        const hasSW = hasWindow && 'serviceWorker' in navigator;
        const hasNotif = hasWindow && 'Notification' in window;

        if (!hasWindow || !hasSW || !hasNotif) return;

        let offOnMessage: (() => void) | null = null;

        const swMsgHandler = (e: MessageEvent) => {
            if ((e?.data as any)?.type === 'NOTIF_DIRTY') trigger();
        };

        let mounted = true;

        (async () => {
            let swReg = await navigator.serviceWorker.getRegistration();

            if (!swReg) swReg = await navigator.serviceWorker.register(SW_PATH);

            const perm = await Notification.requestPermission().catch(
                () => 'default'
            );

            if (!mounted) return;

            setPermission(perm);
            if (perm !== 'granted') return;
            if (!VAPID) return;

            const messaging = await getMessagingIfSupported();

            if (!messaging) return;

            messagingRef.current = messaging;

            const t = await getToken(messaging, {
                vapidKey: VAPID,
                serviceWorkerRegistration: swReg,
            }).catch(() => null);

            if (!mounted || !t) return;

            setToken(t);

            const prev = localStorage.getItem(SUB_KEY);

            if (prev !== t) {
                await notificationService()
                    .subscribeTopic({ token: t, platform })
                    .catch(() => {});
                localStorage.setItem(SUB_KEY, t);
            }

            offOnMessage = onMessage(messaging, payload => {
                const isRoleChangeType =
                    payload?.data?.notificationType ===
                    NotificationTypeEnum.ChangePermission;

                if (isRoleChangeType) {
                    dispatch(setShowRoleChangeNotificationAction(true));

                    void (async () => {
                        await optOut();
                        await fetch('/api/auth/logout', { method: 'POST' });
                    })();
                }

                setLastMessage(payload);
                trigger();
            });

            navigator.serviceWorker.addEventListener?.('message', swMsgHandler);
        })();

        return () => {
            mounted = false;
            offOnMessage?.();
            navigator.serviceWorker.removeEventListener?.(
                'message',
                swMsgHandler
            );
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [enabled, platform, dispatch, trigger, optOut]);

    return { token, permission, lastMessage, optOut };
}
